import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

// Pack sizes the section can be configured with. A size is only offered when the
// matching product is set in the section settings (its data-*-pack-id is non-empty).
const PACK_SIZES = [6, 12, 24];
const PACK_ATTR = { 6: 'six', 12: 'twelve', 24: 'twenty-four' };
const EMPTY_PACK = { id: '', price: 0, comparePrice: 0, sellingPlanId: '' };

class BundleBuilder extends LitElement {
    static get properties() {
        return {
            bundleOptions: { type: Array },
            selectedProducts: { type: Object },
            packs: { type: Object },
            bundleSize: { type: Number },
            isBundleFull: { type: Boolean },
            toastMessage: { type: String },
            showToastOverlay: { type: Boolean },
            isSubscription: { type: Boolean },
            availableProducts: { type: Object },
        }
    }

    constructor() {
        super();
        this.bundleOptions = [];
        this.selectedProducts = {};
        this.packs = {};
        this.bundleSize = 6;
        this.isBundleFull = false;
        this.toastMessage = '';
        this.showToastOverlay = false;
        this.isSubscription = false;
        this.availableProducts = {};
    }

    connectedCallback() {
        super.connectedCallback();
        this.loadBundleOptions();
        this.loadPacks();
        const requested = parseInt(this.getAttribute('data-bundle-size'), 10);
        this.bundleSize = this.sizes.includes(requested) ? requested : (this.sizes[0] || 6);
        this.updateBundleStatus();
    }

    loadPacks() {
        const num = (value) => parseFloat(String(value || '').replace(/,/g, '')) || 0;
        const packs = {};
        PACK_SIZES.forEach(size => {
            const prefix = `data-${PACK_ATTR[size]}-pack`;
            const id = this.getAttribute(`${prefix}-id`);
            if (!id) return;
            packs[size] = {
                id,
                price: num(this.getAttribute(`${prefix}-price`)),
                comparePrice: num(this.getAttribute(`${prefix}-compare-price`)),
                sellingPlanId: this.getAttribute(`${prefix}-subscription-id`) || '',
            };
        });
        this.packs = packs;
    }

    get sizes() {
        return Object.keys(this.packs).map(Number).sort((a, b) => a - b);
    }

    get pack() {
        return this.packs[this.bundleSize] || EMPTY_PACK;
    }

    get nextSize() {
        return this.sizes.find(size => size > this.bundleSize);
    }

    get totalItems() {
        return Object.values(this.selectedProducts).reduce((sum, quantity) => sum + quantity, 0);
    }

    loadBundleOptions() {
        const bundleOptionsJson = this.getAttribute('data-bundle-options');
        if (bundleOptionsJson) {
            this.bundleOptions = JSON.parse(bundleOptionsJson);
            this.availableProducts = this.bundleOptions.reduce((acc, product) => {
                acc[product.id] = product.available === "true";
                return acc;
            }, {});
        }
    }

    render() {
        return html`
            <div class="bundle-builder">
                <h1 class="bundle-title">Build Your Own Bundle</h1>
                <div class="bundle-content">
                    <div class="product-grid">
                        ${this.bundleOptions.map(product => html`
                            <div class="product-item" style="background-color: ${this.selectedProducts[product.id] > 0 ? product.productColor : 'transparent'}">
                            <img src="${product.image}" alt="${product.name}" class="product-image">
                                <div class="product-info">
                                    <h3 class="card__heading h3">${product.name}</h3>
                                    <p class="card__text">${product.shortDescription}</p>
                                </div>
                                ${this.renderProductControl(product)}
                            </div>
                        `)}
                    </div>
                    <div class="selected-bundle">

                        ${this.renderSelectedItems()}
                        ${this.renderVariantSelector()}
                        ${this.renderOkendoReviews()}
                        ${this.renderPrice()}
                        ${this.renderAddToCartButton()}
                    </div>
                </div>
            </div>
            ${this.renderToastOverlay()}
        `;
    }

    renderProductControl(product) {
        const quantity = this.selectedProducts[product.id] || 0;
        const isAvailable = this.availableProducts[product.id];

        if (quantity === 0) {
            return html`
                <a class="button button--primary"
                   @click=${() => this.addToBundle(product)}
                   ?disabled=${this.isBundleFull || !isAvailable}>
                    ${isAvailable ? 'Add to Bundle' : 'Sold Out'}
                </a>
            `;
        } else {
            return html`
                <div class="quantity-selector">
                    <a class="button button--primary" @click=${() => this.decreaseQuantity(product)}>-</a>
                    <span>${quantity}</span>
                    <a class="button button--primary" @click=${() => this.increaseQuantity(product)} ?disabled=${this.isBundleFull}>+</a>
                </div>
            `;
        }
    }

    renderSelectedItems() {
        const selectedItemsCount = this.totalItems;
        const gridSize = this.bundleSize;

        if (selectedItemsCount === 0) {
            return html`
                <div class="selected-items empty-state">
                    <p>Your bundle is empty. Start adding products!</p>
                </div>
            `;
        }

        const selectedItems = Object.entries(this.selectedProducts)
            .flatMap(([id, quantity]) => Array(quantity).fill(id))
            .slice(0, gridSize);

        return html`
            <div class="selected-items-scroll">
                <div class="selected-items-grid size-${gridSize}">
                    ${Array(gridSize).fill().map((_, index) => {
                        if (index < selectedItems.length) {
                            const product = this.bundleOptions.find(p => p.id === selectedItems[index]);
                            return html`
                                <div class="selected-item">
                                    <img src="${product.image}" alt="${product.name}" class="selected-item-image">
                                    <a @click=${() => this.removeProduct(product.id)} class="remove-link">Remove</a>
                                </div>
                            `;
                        } else {
                            return html`
                                <div class="selected-item placeholder">
                                    <img src="https://cdn.shopify.com/s/files/1/0535/0542/5565/files/Chocxo_Placeholder.png?v=1727383915" alt="Placeholder" class="selected-item-image">
                                </div>
                            `;
                        }
                    })}
                </div>
            </div>
        `;
    }

    renderVariantSelector() {
        return html`
            <div class="variant-selector">
                ${this.sizes.map(size => {
                    const pack = this.packs[size];
                    const savings = Math.round(pack.comparePrice - pack.price);
                    return html`
                        <a class="button button--primary ${this.bundleSize === size ? 'active' : ''}" @click=${() => this.setBundleSize(size)}>
                            ${size} Pack
                            ${savings > 0 ? html`<span class="savings-badge">Save $${savings}</span>` : ''}
                        </a>
                    `;
                })}
            </div>
        `;
    }

    renderPrice() {
        const pack = this.pack;
        const discountedPrice = this.calculateTotalPrice();
        const compareAtPrice = pack.comparePrice > pack.price ? pack.comparePrice : pack.price;

        return html`
            <div class="price-subscription-container">
                ${pack.sellingPlanId ? this.renderSubscriptionToggle() : ''}
                <div class="price-container">
                    <span class="current-price">$${discountedPrice.toFixed(2)}</span>
                    ${(compareAtPrice > discountedPrice) ? html`
                        <span class="compare-at-price">
                            $${compareAtPrice.toFixed(2)}
                        </span>
                    ` : ''}
                </div>
            </div>
        `;
    }


    renderOkendoReviews() {
        return html`
            <div class="okendo-reviews">
                <okendo-reviews
                    product-id="${this.productId}"
                    shop-id="${this.shopId}"
                    widget-type="product-reviews"
                ></okendo-reviews>
            </div>
        `
    }



    renderSubscriptionToggle() {
        return html`
            <div class="subscription-toggle">
                <label class="switch">
                    <input type="checkbox" @change=${this.handleSubscriptionToggle} ?checked=${this.isSubscription}>
                    <span class="slider round ${this.isSubscription ? 'active' : ''}"></span>
                </label>
                <span class="checkbox-text">Subscribe & Save (10%)</span>
            </div>
        `;
    }

    handleSubscriptionToggle(e) {
        this.isSubscription = e.target.checked;
        this.requestUpdate();
    }

    calculateTotalPrice() {
        const discountMultiplier = this.isSubscription ? 0.9 : 1; // Apply 10% discount for subscriptions
        return this.pack.price * discountMultiplier;
    }

    addToCart() {
        if (this.totalItems !== this.bundleSize) {
            console.error('Cannot add to cart: Bundle is not complete');
            return;
        }

        const pack = this.pack;
        const bundleProperties = {};

        Object.entries(this.selectedProducts)
            .filter(([_, quantity]) => quantity > 0)
            .forEach(([id, quantity]) => {
                const product = this.bundleOptions.find(p => p.id === id);
                bundleProperties[product.name] = quantity;
            });

        const formData = {
            'items': [{
                'id': pack.id,
                'quantity': 1,
                'properties': bundleProperties
            }]
        };

        if (this.isSubscription && pack.sellingPlanId) {
            // Include the ReCharge selling_plan ID for subscriptions
            formData.items[0].selling_plan = pack.sellingPlanId;
        }

        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
        })
        .then(response => response.json())
        .then(data => {
            // Redirect to cart page after successful add
            window.location.href = '/cart';
        })
        .catch((error) => {
            console.error('Error:', error);
        });
    }

    renderAddToCartButton() {
        const totalItems = this.totalItems;
        const isComplete = totalItems === this.bundleSize;
        return html`
            <button class="button button--primary button--add-to-cart"
                    ?disabled=${!isComplete}
                    @click=${this.addToCart}>
                ${isComplete ? 'Add to Cart' : `Pick ${this.bundleSize - totalItems} More Bags`}
            </button>
        `;
    }

    renderToastOverlay() {
        return html`
            <div class="toast-overlay ${this.showToastOverlay ? 'show' : ''}">
                <div class="toast-message">${this.toastMessage}</div>
            </div>
        `;
    }

    setBundleSize(size) {
        if (!this.packs[size]) return;

        if (size < this.bundleSize) {
            // Switching to a smaller pack: drop excess items
            let totalItems = this.totalItems;

            if (totalItems > size) {
                const newSelectedProducts = { ...this.selectedProducts };

                while (totalItems > size) {
                    // Find a product with quantity > 0 and reduce it
                    for (const [productId, quantity] of Object.entries(newSelectedProducts)) {
                        if (quantity > 0) {
                            newSelectedProducts[productId]--;
                            totalItems--;
                            if (newSelectedProducts[productId] === 0) {
                                delete newSelectedProducts[productId];
                            }
                            break;
                        }
                    }
                }

                this.selectedProducts = newSelectedProducts;
            }
        }

        this.bundleSize = size;
        if (!this.pack.sellingPlanId) {
            this.isSubscription = false;
        }
        this.updateBundleStatus();
        this.requestUpdate();
    }

    addToBundle(product) {
        if (!this.isBundleFull && this.availableProducts[product.id]) {
            this.selectedProducts = {
                ...this.selectedProducts,
                [product.id]: 1
            };
            this.updateBundleStatus();
            this.requestUpdate();
        }
    }

    increaseQuantity(product) {
        if (this.totalItems < this.bundleSize) {
            this.selectedProducts = {
                ...this.selectedProducts,
                [product.id]: (this.selectedProducts[product.id] || 0) + 1
            };
            this.updateBundleStatus();
            this.requestUpdate();
        } else {
            const next = this.nextSize;
            this.showToast(next
                ? `Your ${this.bundleSize}-pack is full! Upgrade to a ${next}-pack for more savings!`
                : 'Oh no, your bundle is full!');
        }
    }


    decreaseQuantity(product) {
        const currentQuantity = this.selectedProducts[product.id] || 0;
        if (currentQuantity > 0) {
            this.selectedProducts = {
                ...this.selectedProducts,
                [product.id]: currentQuantity - 1
            };
            this.updateBundleStatus();
            this.requestUpdate();
        }
    }

    updateBundleStatus() {
        this.isBundleFull = this.totalItems >= this.bundleSize;
    }

    showToast(message) {
        this.toastMessage = message;
        this.showToastOverlay = true;
        this.requestUpdate();

        setTimeout(() => {
            this.showToastOverlay = false;
            this.requestUpdate();
        }, 2000);
    }

    removeProduct(productId) {
        if (this.selectedProducts[productId] && this.selectedProducts[productId] > 0) {
            this.selectedProducts = {
                ...this.selectedProducts,
                [productId]: this.selectedProducts[productId] - 1
            };
            if (this.selectedProducts[productId] === 0) {
                delete this.selectedProducts[productId];
            }
            this.updateBundleStatus();
            this.requestUpdate();
        }
    }

    static get styles() {
        return css`
            .bundle-builder {
                background-color: #f0f0f0;
                border-radius: 8px;
            }

            .bundle-title {
                text-align: center;
                margin-bottom: 20px;
                color: #00263d;
            }

            h2 {
                color: #333;
                margin-bottom: 15px;
                line-height: 1.1;
            }

            .bundle-content {
                display: flex;
                flex-direction: column;
                gap: 20px;
            }

            .product-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                padding: 10px;
            }

            .card__heading {
                margin: 0;
                padding-bottom: 0.5rem;
            }

            .card__text {
                margin-bottom: 0;
            }

            .product-info {
                color: #00263d;
                margin-bottom: 15px;
                line-height: 1.1;
                text-align: center;
                flex-grow: 1;
                display: flex;
                flex-direction: column;
            }

            .selected-bundle {
                background-color: #00263d;
                color: white;
                padding: 15px;
                border-radius: 4px;
                position: sticky;
                bottom: 0;
                left: 0;
                right: 0;
                z-index: 1000;
                border-top: 1px solid #ccc;
                border-radius: 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
            }

            .variant-selector,
            .price,
            .subscription-toggle,
            .button--add-to-cart {
                width: 100%;
                max-width: 300px;
                margin-left: auto;
                margin-right: auto;
            }

            .button--add-to-cart {
                background-color: #fff !important;
                color: #00263d !important;
            }

            .product-item {
                background-color: white;
                padding: 10px;
                border-radius: 4px;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .product-image {
                width: 80%;
                max-height: 300px;
                object-fit: contain;
                margin-bottom: 10px;
                aspect-ratio: 1;
            }

            .button.button--primary {
                background-color: #00263d;
                border: none;
                color: white;
                padding: 10px 20px;
                text-align: center;
                text-decoration: none;
                display: inline-block;
                font-size: 16px;
                margin: 4px 2px;
                cursor: pointer;
                border-radius: 4px;
                transition: background-color 0.3s;
            }

            .button.button--primary:hover {
                background-color: #143a6d;
            }

            .button.button--primary:disabled {
                background-color: #cccccc;
                cursor: not-allowed;
                opacity: 0.6;
            }

            .quantity-selector {
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #00263d;
                border-radius: 20px;
                padding: 5px;
                color: white;
            }

            .quantity-selector .button.button--primary {
                padding: 5px 10px;
                font-size: 18px;
                width: 40px;
                height: 30px;
                background-color: transparent;
                border: none;
            }

            .quantity-selector span {
                margin: 0 10px;
                font-size: 18px;
            }

            .selected-items {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            .selected-item {
                display: flex;
                align-items: center;
                gap: 10px;
                flex: 0 0 auto;
                width: 100px;
                height: 100px;
                justify-content: center;
                border-radius: 4px;
                overflow: hidden;
                position: relative;
                flex-direction: column;
            }

            .selected-item-image {
                width: 100%;
                height: 100%;
                object-fit: contain;
            }

            .variant-selector {
                display: flex;
                justify-content: space-between;
                margin-bottom: 15px;
            }

            .variant-selector .button.button--primary {
                flex: 1;
                position: relative;
                border: 1px solid #fff;
            }

            .variant-selector .button.button--primary.active {
                background-color: #fff;
                color: #00263d;
            }

            .savings-badge {
                position: absolute;
                top: -15px;
                right: -10px;
                background-color: #fff;
                color: #00263d;
                padding: 5px 10px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                border: 1px solid #00263d;
            }

            .subscription-toggle {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .checkbox-label {
                display: flex;
                align-items: center;
                font-size: 16px;
                color: #fff;
            }

            .checkbox-label input[type="checkbox"] {
                margin-right: 10px;
            }

            .subscription-discount-message {
                font-size: 14px;
                color: #fff;
                margin-top: 5px;
            }

            .toast-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.3s, visibility 0.3s;
            }

            .toast-overlay.show {
                opacity: 1;
                visibility: visible;
            }

            .toast-message {
                background-color: white;
                color: red;
                padding: 15px 30px;
                border-radius: 4px;
                font-size: 18px;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }

            .compare-at-price {
                font-size: 1.7rem;
                color: #ccc;
                text-decoration: line-through;
            }

            .current-price {
                font-size: 2rem;
                font-weight: bold;
                color: #fff;
            }

            .selected-items-scroll {
                width: 100%;
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: none;
                -ms-overflow-style: none;
            }

            .selected-items-scroll::-webkit-scrollbar {
                width: 0;
                height: 0;
            }

            .selected-items-grid {
                display: flex;
                flex-direction: row;
                padding: 10px 0;
            }

            .selected-item.placeholder {
                opacity: 0.3;
            }

            .remove-link {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                text-align: center;
                padding: 2px 0;
                font-size: 12px;
                text-decoration: underline;
                cursor: pointer;
            }

            .price-subscription-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-bottom: 15px;
            }

            .price-container {
                display: flex;
                align-items: baseline;
                justify-content: center;
                margin-top: 10px;
            }

            .checkbox-text {
                margin-left: 10px;
                font-size: 1.5rem;
                white-space: nowrap;
            }

            .switch {
                position: relative;
                display: inline-block;
                width: 60px;
                height: 34px;
            }

            .switch input {
                opacity: 0;
                width: 0;
                height: 0;
            }

            .slider {
                position: absolute;
                cursor: pointer;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: #ccc;
                transition: .4s;
                outline: 2px solid white;
            }

            .slider:before {
                position: absolute;
                content: "";
                height: 26px;
                width: 26px;
                left: 4px;
                bottom: 4px;
                background-color: white;
                transition: .4s;
            }

            input:checked + .slider {
                background-color: #00263d;
            }

            input:checked + .slider:before {
                transform: translateX(26px);
            }

            .slider.round {
                border-radius: 34px;
            }

            .slider.round:before {
                border-radius: 50%;
            }

            .slider.active {
                outline: 2px solid white;
            }

            @media (min-width: 769px) {
                .bundle-content {
                    flex-direction: row;
                }

                .product-grid {
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    flex: 2;
                    padding-bottom: 3rem;
                }

                .selected-bundle {
                    z-index: 1;
                    flex: 1;
                    position: sticky;
                    top: 130px;
                    bottom: auto;
                    left: auto;
                    right: auto;
                    align-self: flex-start;
                    border-top: none;
                    border-radius: 4px;
                }

                .selected-items-scroll {
                    overflow-x: visible;
                }

                .selected-items-grid {
                    display: grid;
                    gap: 10px;
                    justify-content: center;
                    max-width: 600px;
                    margin: 0 auto;
                }

                .selected-items-grid.size-6 {
                    grid-template-columns: repeat(3, minmax(100px, 200px));
                    grid-template-rows: repeat(2, auto);
                }

                .selected-items-grid.size-12 {
                    grid-template-columns: repeat(4, minmax(100px, 200px));
                    grid-template-rows: repeat(3, auto);
                }

                .selected-items-grid.size-24 {
                    grid-template-columns: repeat(6, minmax(60px, 100px));
                    grid-template-rows: repeat(4, auto);
                }

                .selected-item {
                    width: 100%;
                    height: auto;
                    aspect-ratio: 1;
                }
            }
        `;
    }
}

customElements.define('bundle-builder', BundleBuilder);